import { Clips } from '@air/api';
import { convertUnknownToError, reportErrorToBugsnag } from '@air/utils-error';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import invariant from 'tiny-invariant';

import { ONE_DAY } from '~/constants/unitsOfTime';
import { useClipTranscriptFetchQueueContext } from '~/providers/ClipTranscriptFetchQueueProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { MAX_TRANSCRIPT_RANGE_IN_MS } from '~/swr-hooks/clips/transcript/constants';
import { type ClipTranscriptCache, isTimeBasedPagination, type TimeRange } from '~/swr-hooks/clips/transcript/types';
import { getDataForTimeInterval } from '~/swr-hooks/clips/transcript/utils/getDataForTimeInterval';

export const getPrivateClipTranscriptSegmentsKey = (clipId: string) => ['PRIVATE_CLIP_TRANSCRIPT_SEGMENTS', { clipId }];

export const useGetPrivateClipTranscriptSegments = ({
  clipId,
  enabled = true,
  videoDurationMs,
}: {
  clipId: string;
  enabled?: boolean;
  videoDurationMs: number;
}) => {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useCurrentWorkspace();
  const [isFetching, setIsFetching] = useState(false);
  const key = useMemo(() => getPrivateClipTranscriptSegmentsKey(clipId), [clipId]);

  const { fetchQueue } = useClipTranscriptFetchQueueContext();

  useEffect(() => {
    fetchQueue.current?.cancelAll();
  }, [clipId, fetchQueue]);

  const fetchClipsSegments = useCallback(
    async ({ startTimeMs, endTimeMs }: TimeRange) => {
      await queryClient.cancelQueries({ queryKey: key });
      invariant(currentWorkspace, 'Current workspace should be defined');
      try {
        const response = await Clips.listTranscriptSegments({
          clipId,
          workspaceId: currentWorkspace.id,
          startTimeMs: Math.round(startTimeMs),
          endTimeMs: Math.round(endTimeMs),
        });

        invariant(isTimeBasedPagination(response.pagination), 'Pagination should be time based');

        // If there is no segment that startTimeMs <= segment.startTime && segment.endTime <= endTimeMs
        // then nextStartTimeMs will be equal to startTimeMs and list will be empty
        // The app should not call for short periods, but if that happens, we want to report it to bugsnag to investigate and fix
        if (response.data.length === 0 && response.pagination.nextStartTimeMs === Math.round(startTimeMs)) {
          reportErrorToBugsnag({
            error: new Error('Too short period in fetch transcript segments request'),
            context: 'Incorrect fetch transcript segment request period',
            metadata: { clipId, startTimeMs, endTimeMs },
          });
        }

        return {
          data: response.data,
          previousEndTimeMs: response.pagination.previousEndTimeMs ?? startTimeMs,
          nextStartTimeMs: response.pagination.nextStartTimeMs ?? videoDurationMs,
        };
      } catch (error) {
        const typedError = convertUnknownToError(error);
        reportErrorToBugsnag({
          error: typedError,
          context: 'Failed to fetch transcript segments',
          metadata: { clipId, startTimeMs, endTimeMs },
        });
        throw typedError;
      }
    },
    [clipId, currentWorkspace, key, queryClient, videoDurationMs],
  );

  const fetchQueryData = useCallback(
    async ({ startTimeMs, endTimeMs }: TimeRange): Promise<ClipTranscriptCache> => {
      const queryData = queryClient.getQueryData<ClipTranscriptCache>(key);

      const newData = await getDataForTimeInterval({
        startTimeMs,
        endTimeMs,
        initialData: queryData?.data ?? [],
        fetchData: fetchClipsSegments,
      });

      const isAllLoaded =
        newData.length === 1 && newData[0].startTimeMs === 0 && newData[0].endTimeMs >= videoDurationMs;

      return {
        data: newData,
        hasMore: !isAllLoaded,
      };
    },
    [fetchClipsSegments, key, queryClient, videoDurationMs],
  );

  const _fetchTranscriptForRange = useCallback(
    async ({ startTimeMs, endTimeMs }: TimeRange) => {
      setIsFetching(true);
      try {
        const newData = await fetchQueryData({ startTimeMs, endTimeMs });
        queryClient.setQueryData(key, newData);
        return newData;
      } finally {
        setIsFetching(false);
      }
    },
    [fetchQueryData, key, queryClient],
  );

  const fetchTranscriptForRange = useCallback(
    (params: TimeRange) =>
      fetchQueue.current?.run(`${params.startTimeMs}-${params.endTimeMs}`, () => _fetchTranscriptForRange(params)),
    [_fetchTranscriptForRange, fetchQueue],
  );

  const { data, error } = useQuery({
    queryKey: key,
    queryFn: () => fetchTranscriptForRange({ startTimeMs: 0, endTimeMs: MAX_TRANSCRIPT_RANGE_IN_MS }),
    staleTime: ONE_DAY,
    enabled: enabled && !!currentWorkspace,
  });

  return {
    key,
    data,
    isFetching,
    isInitialLoading: !data && isFetching && !error,
    fetchTranscriptForRange,
  };
};
